import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import moment from 'moment';
import { Moment } from 'moment';
import { ConsultantService } from '../services/consultant.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { ConsultantSummary } from '../models/consultant-summary.model';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Consultant } from '../models/consultant.model';
import { Department } from '../models/department.model';
import { DepartmentService } from '../services/department.service';
import { ConsultantSummaryDetails } from '../models/consultant-summary-details.model';

export const YEAR_FORMATS = {
  parse: {
    dateInput: 'YYYY',
  },
  display: {
    dateInput: 'YYYY',
    monthYearLabel: 'YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY',
  },
};

@Component({
    selector: 'app-consultant-summary',
    templateUrl: './consultant-summary.component.html',
    styleUrls: ['./consultant-summary.component.scss'],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'da-DK' },
        { provide: MAT_DATE_FORMATS, useValue: YEAR_FORMATS },
    ],
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConsultantSummaryComponent implements OnInit {
  // adapter: DateAdapter<any>;
  loading = true;
  date = new FormControl<Date | Moment>(moment.utc());
  dataSource: MatTableDataSource<ConsultantSummary>;
  totals: ConsultantSummary;

  displayedColumns = ["consultant", "initials", "personalRevenue", "invoicedPersonalRevenue", "weightedPipeline", "leads", "realizedSales"];
  expandedElement: ConsultantSummary | null;
  detailsType: "personalRevenue" | "invoicedPersonalRevenue" | "weightedPipeline" | "leads" | "realizedSales";
  departmentControl = new FormControl();
  departments: Department[];

  details: ConsultantSummaryDetails;
  detailsConsultantId: string;
  loadingRow: ConsultantSummary | null = null;

  private _sorter: MatSort;
  @ViewChild(MatSort)
  set sorter(s: MatSort) {
    this._sorter = s;
    if (s && this.dataSource) {
      this.attachSort(this.dataSource);
    }
  }
  get sorter(): MatSort { return this._sorter; }

  getYear(value: Date | Moment): number {
    return moment.isMoment(value) ? value.year() : value.getFullYear();
  }

  constructor(
    private consultants: ConsultantService,
    public departmentsService: DepartmentService,

  ) {
  }

  async ngOnInit(): Promise<void> {
    this.departments = await this.departmentsService.list();
    await this.loadSummary(this.getYear(this.date.value));

    this.date.valueChanges.subscribe(value => {
      const year = this.getYear(value);
      if (isNaN(year) || year < 2000 || year > 3000) {
        return;
      }
      this.loadSummary(year);
    });

    this.departmentControl.valueChanges.subscribe(async () => {
      await this.loadSummary(this.getYear(this.date.value), this.departmentControl.value);
    });
  }

  private attachSort(ds: MatTableDataSource<ConsultantSummary>): void {
    ds.sortingDataAccessor = (row, property) => {
      if (property === 'initials') return row.consultant.initials ?? '';
      return (row as any)[property];
    };
    ds.sortData = (data: ConsultantSummary[], sort: MatSort): ConsultantSummary[] => {
      const active = sort.active;
      const direction = sort.direction;
      if (!active || direction === '') return data;
      const accessor = ds.sortingDataAccessor;
      const collator = new Intl.Collator('da', { numeric: true, sensitivity: 'base' });
      return [...data].sort((a, b) => {
        const av = accessor(a, active);
        const bv = accessor(b, active);
        let cmp: number;
        if (typeof av === 'string' || typeof bv === 'string') {
          cmp = collator.compare(String(av ?? ''), String(bv ?? ''));
        } else {
          cmp = av - bv;
        }
        return direction === 'asc' ? cmp : -cmp;
      });
    };
    ds.sort = this.sorter;
  }

  async setYear(normalizedMonthAndYear: Moment, datepicker: MatDatepicker<Date>) {
    const year = normalizedMonthAndYear.year();
    this.date.setValue(moment.utc({ year, month: 0, day: 1 }));
    datepicker.close();

    await this.loadSummary(year);
  }

  async loadSummary(year: number, departmentId?: string) {
    let summary = await this.consultants.summary(year);

    if (departmentId) {
      summary = summary.filter(_ => _.consultant.departmentId == departmentId);
    }

    this.totals = new ConsultantSummary(new Consultant("Total", "", "", ""),
      summary.map(_ => _.personalRevenue)?.reduce((prev, curr) => prev + curr, 0),
      summary.map(_ => _.invoicedPersonalRevenue)?.reduce((prev, curr) => prev + curr, 0),
      summary.map(_ => _.weightedPipeline)?.reduce((prev, curr) => prev + curr, 0),
      summary.map(_ => _.leads)?.reduce((prev, curr) => prev + curr, 0),
      summary.map(_ => _.realizedSales)?.reduce((prev, curr) => prev + curr, 0),
    );

    this.dataSource = new MatTableDataSource<ConsultantSummary>(summary);
    this.attachSort(this.dataSource);
    this.loading = false;
  }

  async showDetails(row, type) {
    console.log(row, type);
    // Loading details takes a few seconds; ignore clicks until it finishes.
    if (this.loadingRow) {
      return;
    }
    if (this.expandedElement == row && this.detailsType == type) {
      this.expandedElement = null;
      return;
    }

    this.loadingRow = row;
    try {
      this.details = await this.consultants.summaryDetails(row.consultant.id, this.getYear(this.date.value));
    } finally {
      this.loadingRow = null;
    }
    this.detailsConsultantId = row.consultant.id;
    this.expandedElement = row;
    this.detailsType = type;
  }

  async exportPdf() {
    const year = this.getYear(this.date.value);
    let file = await this.consultants.summaryPdf(year);
    const blob = new Blob([file.body], { type: `application/pdf;` });
    const link = document.createElement('a');
    if (link.download !== undefined) { // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `totaler_${year}.pdf`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  async exportExcel() {
    const year = this.getYear(this.date.value);
    let file = await this.consultants.summaryExcel(year);
    const blob = new Blob([file.body], { type: `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;` });
    const link = document.createElement('a');
    if (link.download !== undefined) { // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `totaler_${year}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
